import { errorToast } from 'v2/utils/toasts'
import type { GenericEdge, GenericNode } from '../../types'
import { isAiAgentOrActionNode } from '../../utils/ai-agent-utils'
import { handleEvalStreamError, prepareEvalSidebarForRun, startEvalStream } from './eval-stream'
import { useEvalStore } from './store'
import { EvalStatus } from './types'

/**
 * Updates a node's eval status metadata
 */
const updateNodeEvalMeta = (
  node: GenericNode,
  status: EvalStatus | null,
  passedCount?: number,
  totalCount?: number,
): GenericNode => {
  if (!node.data?.meta?.evalStatus && status === null) return node

  return {
    ...node,
    data: {
      ...node.data,
      meta: {
        ...node.data.meta,
        evalStatus: status || undefined,
        evalPassedCount: status ? passedCount : undefined,
        evalTotalCount: status ? totalCount : undefined,
      },
    },
  }
}

/**
 * Updates eval status on all nodes
 * If evalResults is provided: applies status based on eval results
 * If evalResults is empty/undefined: clears all eval status from nodes
 */
export const updateEvalStatusOnNodes = (
  nodes: GenericNode[],
  evalResults?: Array<{
    nodeUuid: string
    status?: EvalStatus
    testCases: Array<{
      status: EvalStatus.Pending | EvalStatus.Testing | EvalStatus.Passed | EvalStatus.Failed
    }>
  }>,
  currentlyTestingNodeUuid?: string | null,
  isEvalRunning?: boolean,
): GenericNode[] => {
  // If no eval results provided, clear all eval status
  if (!evalResults || evalResults.length === 0) {
    return nodes.map((node) => updateNodeEvalMeta(node, null))
  }

  // Apply eval status based on results
  return nodes.map((node) => {
    // Find eval result for this node
    const evalNode = evalResults.find((en) => en.nodeUuid === node.id)
    if (!evalNode) return node

    const statusInfo = getNodeEvalStatus(
      evalNode,
      currentlyTestingNodeUuid || null,
      isEvalRunning || false,
    )

    return updateNodeEvalMeta(
      node,
      statusInfo?.status || null,
      statusInfo?.passedCount,
      statusInfo?.totalCount,
    )
  })
}

/**
 * Calculate node status based on test cases
 */
export const calculateNodeStatus = (
  testCases: Array<{
    status: EvalStatus
  }>,
): {
  status: EvalStatus
  passedCount: number
  totalCount: number
} | null => {
  const passedCount = testCases.filter((tc) => tc.status === EvalStatus.Passed).length
  const failedCount = testCases.filter((tc) => tc.status === EvalStatus.Failed).length
  const totalCount = testCases.length

  if (totalCount === 0) return null

  // All passed
  if (passedCount === totalCount) {
    return { status: EvalStatus.Passed, passedCount, totalCount }
  }

  // All failed
  if (failedCount === totalCount) {
    return { status: EvalStatus.Failed, passedCount, totalCount }
  }

  // Partial pass
  return { status: EvalStatus.Partial, passedCount, totalCount }
}

/**
 * Helper: Calculate node eval status badge info
 */
const getNodeEvalStatus = (
  node: {
    nodeUuid: string
    status?: EvalStatus
    testCases: Array<{
      status: EvalStatus
    }>
  },
  currentlyTestingNodeUuid: string | null,
  isEvalRunning: boolean,
): {
  status: EvalStatus
  passedCount: number
  totalCount: number
} | null => {
  // Don't show results if this node is currently being tested
  if (isEvalRunning && currentlyTestingNodeUuid === node.nodeUuid) {
    return null
  }

  // Only show results if node has completed status (passed or failed)
  if (node.status !== EvalStatus.Passed && node.status !== EvalStatus.Failed) return null

  return calculateNodeStatus(node.testCases)
}

/**
 * Validates nodes for eval execution
 * @param nodes Array of nodes to validate
 * @returns true if validation passes, false otherwise
 */
export const validateNodesForEval = (nodes: GenericNode[]): boolean => {
  const hasAiActionOrAgentV2 = nodes.some((node) => isAiAgentOrActionNode(node.type))

  if (!hasAiActionOrAgentV2) {
    errorToast({
      title: 'Simulation Error',
      description: 'Add at least one AI action or AI agent node before running a simulation.',
    })
    return false
  }

  return true
}

export interface EvalExecutionOptions {
  nodes: GenericNode[]
  edges: GenericEdge[]
  evalId?: string | null
}

/**
 * Unified function to execute eval operations
 * Handles validation, data clearing, sidebar preparation, and eval stream execution
 */
export const executeEval = (options: EvalExecutionOptions) => {
  const { nodes, edges, evalId = null } = options

  const isResume = evalId !== null
  const shouldClearData = !isResume

  // Validation
  const isValid = validateNodesForEval(nodes)
  if (!isValid) {
    return
  }

  // Clear existing data
  if (shouldClearData) {
    useEvalStore.getState().clearEvalStream()
    prepareEvalSidebarForRun()
  }

  // Execute eval stream
  try {
    const evalIdToUse = isResume ? evalId : undefined
    startEvalStream(nodes, edges, evalIdToUse)
  } catch (error) {
    handleEvalStreamError(error)

    const errorMessages = 'Failed to start simulation.'
    errorToast({
      title: 'Simulation Error',
      description: errorMessages,
    })
  }
}
